import { useMemo, useState } from 'react';
import { PrimaryButton, SecondaryButton } from '@entur/button';
import { Checkbox } from '@entur/form';
import { Modal } from '@entur/modal';
import { useIntl } from 'react-intl';
import ButtonRow from 'components/ButtonRow';
import styles from '../styles.module.scss';
import { FilterModalColumn } from '../hooks/useClientFilter';
import { buildColumnFilterOptions } from '../tableFeatures';

interface ClientFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  column: FilterModalColumn;
  /** All current rows (unfiltered for this column) to derive unique values */
  allRows: object[];
  currentValues: unknown[];
  onSave: (columnKey: string, values: unknown[]) => void;
  onReset: (columnKey: string) => void;
}

export const ClientFilterModal = ({
  isOpen,
  onClose,
  column,
  allRows,
  currentValues,
  onSave,
  onReset,
}: ClientFilterModalProps) => {
  const intl = useIntl();

  const uniqueValues = useMemo(
    () =>
      buildColumnFilterOptions(column.key, column.config, allRows, intl.formatMessage({ id: 'generalValues.noValue' })),
    [allRows, column, intl]
  );

  // No active filter → pre-select all values (all = no filter, same semantics)
  const [selected, setSelected] = useState<Set<unknown>>(
    new Set(currentValues.length === 0 ? new Set(uniqueValues.keys()) : currentValues)
  );

  const toggleValue = (val: unknown) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(val) ? next.delete(val) : next.add(val);
      return next;
    });
  };

  const allChecked = uniqueValues.size > 0 && Array.from(uniqueValues.keys()).every((v) => selected.has(v));

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(uniqueValues.keys()));
  };

  return (
    <Modal open={isOpen} onDismiss={onClose} size="large" title={column.label}>
      <div className={styles.filterModal}>
        <div className={styles.checkField}>
          <div className={styles.checkboxList}>
            <Checkbox checked={allChecked} onChange={toggleAll}>
              {intl.formatMessage({ id: 'generalValues.selectAll' })}
            </Checkbox>
          </div>
          {Array.from(uniqueValues.keys())
            .sort()
            .map((val) => (
              <div className={styles.checkboxList} key={String(val)}>
                <Checkbox checked={selected.has(val)} onChange={() => toggleValue(val)}>
                  {uniqueValues.get(val)}
                </Checkbox>
              </div>
            ))}
        </div>

        <ButtonRow>
          <ButtonRow.RightGroup>
            <SecondaryButton
              onClick={() => {
                onReset(column.key);
                onClose();
              }}
            >
              {intl.formatMessage({ id: 'generalValues.reset' })}
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                // All values selected = semantically no filter; treat as reset
                if (Array.from(uniqueValues.keys()).every((v) => selected.has(v))) {
                  onReset(column.key);
                } else {
                  onSave(column.key, Array.from(selected));
                }
                onClose();
              }}
            >
              {intl.formatMessage({ id: 'generalValues.save' })}
            </PrimaryButton>
          </ButtonRow.RightGroup>
        </ButtonRow>
      </div>
    </Modal>
  );
};
